/**
 * AI Chat Window Store — Zustand store for floating AI Library Chat window
 *
 * Manages window state: open/close, minimize, position, size, selected references.
 * Persists UI state (position, size) in localStorage.
 */

import { create } from 'zustand';
import { safeSetItem } from '../utils/safeLocalStorage';

// ─── Types ────────────────────────────────────────────────

interface AiChatWindowState {
  isOpen: boolean;
  isMinimized: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  /** Work IDs selected for the chat context (snapshot from page selection or in-window manager) */
  selectedWorkIds: Set<string>;
  /** True once the user has explicitly added/removed references (vs default "all with highlights") */
  hasExplicitSelection: boolean;
  /** When set, opens directly to Conversas Salvas filtered by this reference */
  filterByReferenceId: string | null;
  /** Label shown in filter header (e.g. "Bentegeac et al. (2025)") */
  filterByReferenceLabel: string | null;
  /** F2 (fix 3/jun) — sinais do AiReaderPanel. Docado e flutuante são MOUNTS
   *  SEPARADOS (não re-parent): "soltar" desmonta e remonta. Este canal
   *  sobrevive ao remount: o gate de "soltar" (index.tsx) vê a geração ativa
   *  INTEIRA — isLoading (do disparo ao fim, fase pré-stream inclusa) +
   *  isStreaming (smoke 4/jun: soltar na janela pré-stream matava a chamada e
   *  re-disparava outra) —, e a instância remontada rehidrata o rascunho. */
  readerStreamingWorkId: string | null;
  /** Rascunho do input do reader por workId — sessão apenas, não persiste. */
  readerDrafts: Record<string, string>;

  open: (checkedWorkIds?: Set<string>) => void;
  openFilteredByReference: (referenceId: string, label: string) => void;
  clearReferenceFilter: () => void;
  close: () => void;
  minimize: () => void;
  restore: () => void;
  setPosition: (x: number, y: number) => void;
  setSize: (width: number, height: number) => void;
  addWorkId: (workId: string) => void;
  removeWorkId: (workId: string) => void;
  setSelectedWorkIds: (ids: Set<string>) => void;
  setReaderStreaming: (workId: string, streaming: boolean) => void;
  setReaderDraft: (workId: string, draft: string) => void;
}

// ─── Constants ────────────────────────────────────────────

const STORAGE_KEY = 'bigwa-ai-chat-window-ui';

const DEFAULT_SIZE = { width: 480, height: 600 };

const DEFAULT_POSITION = () => ({
  x: Math.max(60, window.innerWidth - 520),
  y: Math.max(40, window.innerHeight - 660),
});

// ─── localStorage Persistence ──────────────────────────────

function loadUIState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        position: parsed.position || DEFAULT_POSITION(),
        size: parsed.size || DEFAULT_SIZE,
      };
    }
  } catch {
    // fallback to defaults
  }
  return {
    position: DEFAULT_POSITION(),
    size: DEFAULT_SIZE,
  };
}

function saveUIState(
  position: { x: number; y: number },
  size: { width: number; height: number },
) {
  try {
    safeSetItem(STORAGE_KEY, JSON.stringify({ position, size }));
  } catch {
    // best-effort
  }
}

// ─── Store ────────────────────────────────────────────────

const initialUI = loadUIState();

export const useAiChatWindowStore = create<AiChatWindowState>((set, get) => ({
  isOpen: false,
  isMinimized: false,
  position: initialUI.position,
  size: initialUI.size,
  selectedWorkIds: new Set(),
  hasExplicitSelection: false,
  filterByReferenceId: null,
  filterByReferenceLabel: null,
  readerStreamingWorkId: null,
  readerDrafts: {},

  open: (checkedWorkIds?: Set<string>) => {
    const hasChecked = checkedWorkIds && checkedWorkIds.size > 0;
    set({
      isOpen: true,
      isMinimized: false,
      filterByReferenceId: null,
      filterByReferenceLabel: null,
      selectedWorkIds: hasChecked ? new Set(checkedWorkIds) : new Set(),
      hasExplicitSelection: true,
    });
  },

  openFilteredByReference: (referenceId: string, label: string) => {
    set({
      isOpen: true,
      isMinimized: false,
      filterByReferenceId: referenceId,
      filterByReferenceLabel: label,
    });
  },

  clearReferenceFilter: () => {
    set({ filterByReferenceId: null, filterByReferenceLabel: null });
  },

  close: () => {
    set({ isOpen: false, isMinimized: false, selectedWorkIds: new Set(), hasExplicitSelection: false, filterByReferenceId: null, filterByReferenceLabel: null });
  },

  minimize: () => {
    set({ isMinimized: true });
  },

  restore: () => {
    set({ isMinimized: false });
  },

  setPosition: (x: number, y: number) => {
    const pos = { x, y };
    set({ position: pos });
    saveUIState(pos, get().size);
  },

  setSize: (width: number, height: number) => {
    const sz = { width, height };
    set({ size: sz });
    saveUIState(get().position, sz);
  },

  addWorkId: (workId: string) => {
    const next = new Set(get().selectedWorkIds);
    next.add(workId);
    set({ selectedWorkIds: next, hasExplicitSelection: true });
  },

  removeWorkId: (workId: string) => {
    const next = new Set(get().selectedWorkIds);
    next.delete(workId);
    set({ selectedWorkIds: next, hasExplicitSelection: true });
  },

  setSelectedWorkIds: (ids: Set<string>) => {
    set({ selectedWorkIds: new Set(ids), hasExplicitSelection: true });
  },

  // ── F2 (fix 3/jun) — sinais do AiReaderPanel (ver comentário no tipo) ──

  setReaderStreaming: (workId: string, streaming: boolean) => {
    if (streaming) {
      set({ readerStreamingWorkId: workId });
      return;
    }
    // Só o dono do sinal limpa — unmount tardio de outra obra não atropela.
    if (get().readerStreamingWorkId === workId) {
      set({ readerStreamingWorkId: null });
    }
  },

  setReaderDraft: (workId: string, draft: string) => {
    set({ readerDrafts: { ...get().readerDrafts, [workId]: draft } });
  },
}));
